import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  useArchiveExpenseCategory,
  useCreateExpenseCategory,
  useDeleteExpenseCategory,
  useRenameExpenseCategory,
  useUnarchiveExpenseCategory,
} from '@/api/expense-categories'
import { useExpenseCategories } from '@/api/expenses'
import type { ExpenseCategory } from '@/api/expenses'
import { useWorkspace } from '@/api/workspace'
import { categoryErrorMessage } from '@/components/expenses/category-error-message'
import { categoryLabel } from '@/components/expenses/category-label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Expense categories (UI-SCREENS E-03, 07b #195), Owner only — Settings is an Owner area and the
// API answers Staff with 403 either way.
//
// The row is the identity and the name only a label: a rename shows on every past Expense. A
// **used** category — any Expense references it, soft-deleted and voided ones included (Q3) —
// can only be archived; an unused one can be deleted. The reserved "Material restock" row is
// read-only in full (Q1): it is what every Restock Expense resolves.
export const Route = createFileRoute('/w/$slug/settings/expense-categories')({
  component: ExpenseCategoriesSettings,
  staticData: { breadcrumb: 'settings.expenseCategories.title' },
})

function ExpenseCategoriesSettings() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  // Already loaded by the workspace layout; Staff has no settings screens (the API refuses too).
  const workspace = useWorkspace(slug)
  const categories = useExpenseCategories(slug)
  const [errorMessage, setErrorMessage] = useState('')
  const [renaming, setRenaming] = useState<ExpenseCategory | null>(null)
  const [deleting, setDeleting] = useState<ExpenseCategory | null>(null)
  const archive = useArchiveExpenseCategory()
  const unarchive = useUnarchiveExpenseCategory()
  const remove = useDeleteExpenseCategory()

  if (workspace.data && workspace.data.role !== 'owner') return <Navigate to="/w/$slug" params={{ slug }} replace />

  const rows = categories.data ?? []

  return (
    <section className="flex max-w-3xl flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-semibold">{t('settings.expenseCategories.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('settings.expenseCategories.subtitle')}</p>
      </div>

      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {errorMessage}
      </p>

      <AddCategoryForm slug={slug} onError={setErrorMessage} />

      {categories.isPending ? (
        <LoadingSkeleton />
      ) : categories.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : rows.length === 0 ? (
        <RecordListEmpty>{t('settings.expenseCategories.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {rows.map((category) => (
            <RecordListItem
              key={category.id}
              title={categoryLabel(t, category.name)}
              badges={
                <>
                  {category.system ? <Badge variant="secondary">{t('settings.expenseCategories.reserved')}</Badge> : null}
                  {category.archived_at ? (
                    <Badge variant="outline">{t('settings.expenseCategories.archived')}</Badge>
                  ) : null}
                  {category.used ? <Badge variant="outline">{t('settings.expenseCategories.used')}</Badge> : null}
                </>
              }
              description={
                category.system
                  ? t('settings.expenseCategories.reservedHint')
                  : category.archived_at
                    ? t('settings.expenseCategories.archivedHint')
                    : category.used
                      ? t('settings.expenseCategories.usedHint')
                      : t('settings.expenseCategories.unusedHint')
              }
              actions={
                category.can.update || category.can.archive || category.can.unarchive || category.can.delete ? (
                  <>
                    {category.can.update ? (
                      <Button type="button" variant="outline" size="sm" onClick={() => setRenaming(category)}>
                        {t('settings.expenseCategories.rename')}
                      </Button>
                    ) : null}
                    {category.can.archive ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={archive.isPending}
                        onClick={() => {
                          setErrorMessage('')
                          archive.mutate(
                            { params: { path: { slug, expense_category_id: category.id } } },
                            { onError: (error) => setErrorMessage(categoryErrorMessage(t, error)) },
                          )
                        }}
                      >
                        {t('settings.expenseCategories.archive')}
                      </Button>
                    ) : null}
                    {category.can.unarchive ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={unarchive.isPending}
                        onClick={() => {
                          setErrorMessage('')
                          unarchive.mutate(
                            { params: { path: { slug, expense_category_id: category.id } } },
                            { onError: (error) => setErrorMessage(categoryErrorMessage(t, error)) },
                          )
                        }}
                      >
                        {t('settings.expenseCategories.unarchive')}
                      </Button>
                    ) : null}
                    {category.can.delete ? (
                      <Button type="button" variant="outline" size="sm" onClick={() => setDeleting(category)}>
                        {t('settings.expenseCategories.delete')}
                      </Button>
                    ) : null}
                  </>
                ) : undefined
              }
            />
          ))}
        </RecordList>
      )}

      {renaming ? (
        <RenameDialog
          slug={slug}
          category={renaming}
          onClose={() => setRenaming(null)}
          onError={setErrorMessage}
        />
      ) : null}

      {deleting ? (
        <AlertDialog open onOpenChange={(open) => (open ? null : setDeleting(null))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('settings.expenseCategories.deleteTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('settings.expenseCategories.deleteDescription')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('settings.expenseCategories.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const category = deleting
                  setDeleting(null)
                  setErrorMessage('')
                  remove.mutate(
                    { params: { path: { slug, id: category.id } } },
                    { onError: (error) => setErrorMessage(categoryErrorMessage(t, error)) },
                  )
                }}
              >
                {t('settings.expenseCategories.deleteConfirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </section>
  )
}

// Adding a category: the name is trimmed and required, and the server refuses one that another
// category already uses, ignoring case and archived ones.
function AddCategoryForm({ slug, onError }: { slug: string; onError: (message: string) => void }) {
  const { t } = useTranslation()
  const create = useCreateExpenseCategory()
  const schema = z.object({ name: z.string().trim().min(1, { message: t('settings.expenseCategories.required') }) })
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { name: '' } })

  return (
    <form
      className="flex flex-col gap-2 sm:flex-row sm:items-end"
      noValidate
      onSubmit={form.handleSubmit((values) => {
        onError('')
        create.mutate(
          { params: { path: { slug } }, body: { expense_category: { name: values.name.trim() } } },
          {
            onSuccess: () => form.reset({ name: '' }),
            onError: (error) => onError(categoryErrorMessage(t, error)),
          },
        )
      })}
    >
      <div className="flex flex-1 flex-col gap-2">
        <Label htmlFor="new-category">{t('settings.expenseCategories.nameLabel')}</Label>
        <Input id="new-category" className="h-11" autoComplete="off" {...form.register('name')} />
        <p role="alert" className="text-sm text-destructive empty:hidden">
          {form.formState.errors.name?.message ?? ''}
        </p>
      </div>
      <Button type="submit" className="h-11" disabled={create.isPending}>
        {t(create.isPending ? 'settings.expenseCategories.saving' : 'settings.expenseCategories.add')}
      </Button>
    </form>
  )
}

// Renaming applies everywhere the category is shown, past Expenses included.
function RenameDialog({
  slug,
  category,
  onClose,
  onError,
}: {
  slug: string
  category: ExpenseCategory
  onClose: () => void
  onError: (message: string) => void
}) {
  const { t } = useTranslation()
  const rename = useRenameExpenseCategory()
  const schema = z.object({ name: z.string().trim().min(1, { message: t('settings.expenseCategories.required') }) })
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: category.name },
  })

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={form.handleSubmit((values) => {
            onError('')
            rename.mutate(
              {
                params: { path: { slug, id: category.id } },
                body: { expense_category: { name: values.name.trim() } },
              },
              {
                onSuccess: () => onClose(),
                onError: (error) => {
                  onError(categoryErrorMessage(t, error))
                  onClose()
                },
              },
            )
          })}
        >
          <DialogHeader>
            <DialogTitle>{t('settings.expenseCategories.renameTitle')}</DialogTitle>
            <DialogDescription>{t('settings.expenseCategories.renameDescription')}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            <Label htmlFor="rename-category">{t('settings.expenseCategories.nameLabel')}</Label>
            <Input id="rename-category" className="h-11" autoComplete="off" {...form.register('name')} />
            <p role="alert" className="text-sm text-destructive empty:hidden">
              {form.formState.errors.name?.message ?? ''}
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" className="h-11" onClick={onClose}>
              {t('settings.expenseCategories.cancel')}
            </Button>
            <Button type="submit" className="h-11" disabled={rename.isPending}>
              {t(rename.isPending ? 'settings.expenseCategories.saving' : 'settings.expenseCategories.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
